import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

const storageKey = (uid) => `qg_msg_last_read_${uid}`;
const POLL_MS = 30_000;

export function useMessageNotifications() {
  const { user, profile } = useAuth();
  const [conversations, setConversations] = useState([]);
  const intervalRef = useRef(null);
  const channelRef = useRef(null);

  const getLastRead = useCallback(() => {
    if (!user?.id) return new Date(0).toISOString();
    return (
      localStorage.getItem(storageKey(user.id)) ||
      new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()
    );
  }, [user?.id]);

  const fetchUnread = useCallback(async () => {
    if (!user?.id || !profile) return;
    const since = getLastRead();

    try {
      const { data: msgs } = await supabase
        .from('messages')
        .select(`
          id, content, sender_id, thread_id, created_at,
          sender:profiles!messages_sender_id_fkey(id, full_name, role),
          thread:job_threads(id, thread_type, job_id, service_request_id)
        `)
        .neq('sender_id', user.id)
        .gt('created_at', since)
        .order('created_at', { ascending: false })
        .limit(50);

      if (!msgs || msgs.length === 0) { setConversations([]); return; }

      const jobIds = [...new Set(msgs.filter(m => m.thread?.job_id).map(m => m.thread.job_id))];
      const srIds  = [...new Set(msgs.filter(m => m.thread?.service_request_id).map(m => m.thread.service_request_id))];

      const [jobsRes, srsRes] = await Promise.all([
        jobIds.length ? supabase.from('jobs').select('id, title').in('id', jobIds) : Promise.resolve({ data: [] }),
        srIds.length  ? supabase.from('service_requests').select('id, title').in('id', srIds) : Promise.resolve({ data: [] }),
      ]);

      const jobMap = Object.fromEntries((jobsRes.data || []).map(j => [j.id, j]));
      const srMap  = Object.fromEntries((srsRes.data  || []).map(s => [s.id, s]));

      const rolePath =
        profile.role === 'sales_rep' ? 'sales' :
        profile.role === 'admin'     ? 'admin' :
        profile.role === 'client'    ? 'client' :
        profile.role === 'contractor'? 'contractor' : 'client';

      const grouped = {};
      msgs.forEach(m => {
        const isJob = !!m.thread?.job_id;
        const ctxId = isJob ? m.thread.job_id : m.thread?.service_request_id;
        if (!ctxId) return;

        const key = `${isJob ? 'job' : 'sr'}_${ctxId}`;
        if (grouped[key]) return;

        const title = isJob
          ? (jobMap[ctxId]?.title || 'Job')
          : (srMap[ctxId]?.title || 'Service Request');

        let navTo;
        if (isJob) {
          navTo = `/dashboard/${rolePath}/jobs/${ctxId}`;
        } else if (profile.role === 'admin') {
          navTo = `/dashboard/admin/services/${ctxId}`;
        } else if (profile.role === 'sales_rep') {
          navTo = `/dashboard/sales/requests/${ctxId}`;
        } else {
          navTo = `/dashboard/client/services/${ctxId}`;
        }

        grouped[key] = {
          key,
          title,
          ctxType: isJob ? 'job' : 'service_request',
          latestMsg: m,
          count: 0,
          navTo,
          navState: isJob ? { activeTab: 'messages' } : { activeTab: 'chat' },
        };
        grouped[key].count = msgs.filter(x => {
          const xId = x.thread?.job_id || x.thread?.service_request_id;
          return xId === ctxId;
        }).length;
      });

      setConversations(Object.values(grouped));
    } catch (err) {
      console.error('[useMessageNotifications] fetch error:', err);
    }
  }, [user?.id, profile, getLastRead]);

  const markAllRead = useCallback(() => {
    if (!user?.id) return;
    localStorage.setItem(storageKey(user.id), new Date().toISOString());
    setConversations([]);
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id || !profile?.id) return;

    fetchUnread();
    intervalRef.current = setInterval(fetchUnread, POLL_MS);

    channelRef.current = supabase
      .channel(`msg_notify_${user.id}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages' }, (payload) => {
        if (payload.new?.sender_id !== user.id) {
          fetchUnread();
        }
      })
      .subscribe();

    return () => {
      clearInterval(intervalRef.current);
      if (channelRef.current) supabase.removeChannel(channelRef.current);
    };
  }, [user?.id, profile?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    conversations,
    unreadCount: conversations.reduce((sum, c) => sum + c.count, 0),
    markAllRead,
    refetch: fetchUnread,
  };
}
